import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEmbedContext } from './useEmbedContext';

/**
 * Synchronizes the child app's route with the parent shell URL for deep linking.
 *
 * - Reports route changes to the shell via ROUTE_CHANGE messages
 * - Handles incoming NAVIGATE messages from the shell (back/forward)
 * - Prevents echo loops between the two directions
 */
export function useRouteSync() {
  const { isEmbedded, bridge, isReady, registerNavigateHandler } = useEmbedContext();
  const location = useLocation();
  const navigate = useNavigate();
  const isExternalNav = useRef(false);
  const lastReported = useRef(location.pathname + location.search + location.hash);

  // Register navigate handler for incoming NAVIGATE messages from shell
  useEffect(() => {
    if (!isEmbedded || !registerNavigateHandler) return;
    registerNavigateHandler((path: string) => {
      isExternalNav.current = true;
      lastReported.current = path;
      navigate(path);
    });
  }, [isEmbedded, registerNavigateHandler, navigate]);

  // Report route changes to shell
  useEffect(() => {
    if (!isEmbedded || !bridge || !isReady) return;
    if (isExternalNav.current) {
      isExternalNav.current = false;
      return;
    }
    const currentPath = location.pathname + location.search + location.hash;
    if (currentPath !== lastReported.current) {
      lastReported.current = currentPath;
      bridge.reportRouteChange(currentPath);
    }
  }, [location, isEmbedded, bridge, isReady]);
}
